<script setup lang="ts">
/**
 * AdminImports - Import jobs list with slideover details.
 *
 * Hash navigation scheme:
 *   #job-{uuid}   - Opens import job detail slideover
 *   #brand-{slug} - Opens brand detail slideover (for linked brands)
 *
 * Note: This uses prefixed hashes (#job-, #brand-) to disambiguate entity types,
 * unlike AdminBrands.vue which uses bare slugs (#my-brand). The prefix pattern
 * is preferred when a view can open multiple entity types.
 */
import {
  h,
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  resolveComponent,
  type Component,
} from 'vue'
import { useImports } from '@/stores/imports'
import { useAdmin } from '@/stores/admin'
import type { DataImportJob, DataImportJobDetail } from '@/stores/imports'
import type { Brand } from '@/stores/admin'
import type { TableColumn } from '@nuxt/ui'
import { handleError } from '@/lib/error'
import {
  formatPlatformName,
  getImportStatusColor,
  formatRelativeTime,
  buildCloudRunUrl,
  buildCloudRunLogsUrl,
  buildApifyUrl,
  type ImportJobStatus,
} from '@/lib/utils'
import AdminImportDetail from '@/views/AdminImportDetail.vue'
import AdminBrandDetail from '@/views/AdminBrandDetail.vue'
import PlatformBadge from '@/components/PlatformBadge.vue'
import SmartDateFilter from '@/components/SmartDateFilter.vue'
import { useSortableTable } from '@/composables/useSortableTable'
import { useUrlFilters } from '@/composables/useUrlFilters'
import { format, subDays, subWeeks, subMonths, lastDayOfMonth } from 'date-fns'
import { activeFilterUi, sortSelectedFirst } from '@/lib/filterUtils'
import { createCopyableIdCell } from '@/lib/tableHelpers'

/**
 * Convert SmartDateFilter (operator, date, dateEnd) to {from, to} bounds.
 * Used to normalize all date filter operators to a simple range for the backend.
 */
function resolveDateBounds(
  operator: string,
  date: string,
  dateEnd: string,
): { from: string | undefined; to: string | undefined } {
  if (!operator) return { from: undefined, to: undefined }

  const today = format(new Date(), 'yyyy-MM-dd')

  switch (operator) {
    case 'between':
      return { from: date || undefined, to: dateEnd || undefined }
    case 'before':
      return { from: '1970-01-01', to: date || undefined }
    case 'on_or_after':
      return { from: date || undefined, to: '2099-12-31' }
    case 'on_the_day':
      return { from: date || undefined, to: date || undefined }
    case 'in_the_past': {
      // date format: "7_days", "2_weeks", "3_months"
      const match = date.match(/^(\d+)_(days|weeks|months)$/)
      if (!match) return { from: undefined, to: undefined }
      const n = parseInt(match[1], 10)
      const unit = match[2]
      const now = new Date()
      let fromDate: Date
      if (unit === 'days') fromDate = subDays(now, n)
      else if (unit === 'weeks') fromDate = subWeeks(now, n)
      else fromDate = subMonths(now, n)
      return { from: format(fromDate, 'yyyy-MM-dd'), to: today }
    }
    case 'in_the_year':
      // date format: "2024"
      return { from: `${date}-01-01`, to: `${date}-12-31` }
    case 'in_the_month': {
      // date format: "2024-06"
      const monthStart = `${date}-01`
      const monthEnd = format(lastDayOfMonth(new Date(`${date}-01`)), 'yyyy-MM-dd')
      return { from: monthStart, to: monthEnd }
    }
    default:
      return { from: undefined, to: undefined }
  }
}

const imports = useImports()
const admin = useAdmin()

const UBadge = resolveComponent('UBadge')
const UButton = resolveComponent('UButton') as Component
const copyableId = createCopyableIdCell(UButton)

// Loading states
const isLoading = ref(true)

// Search and filters
const searchQuery = ref('')
const selectedPlatforms = ref<string[]>([])
const selectedMethods = ref<string[]>([])
// Use string[] since URL round-trip loses type info; validated via validStatuses Set
const selectedStatuses = ref<string[]>([])
const selectedCountries = ref<string[]>([])
const selectedSectors = ref<string[]>([])
const selectedClients = ref<string[]>([])
// Smart date filter for completed_ts
const completedOp = ref('')
const completedDate = ref('')
const completedDateEnd = ref('')
// Smart date filter for started_ts
const startedOp = ref('')
const startedDate = ref('')
const startedDateEnd = ref('')
const numAdsFilter = ref<'has_ads' | 'no_ads' | ''>('')
// Data range filter (date_from/date_to intersection)
const dataRangeOp = ref('')
const dataRangeFrom = ref('')
const dataRangeTo = ref('')
const showMoreFilters = ref(false)

// Resolved data range bounds (converts SmartDateFilter operator to from/to)
const resolvedDataRange = computed(() =>
  resolveDateBounds(dataRangeOp.value, dataRangeFrom.value, dataRangeTo.value),
)

// Count active filters in the hidden section
const hiddenFiltersCount = computed(() => {
  let count = 0
  if (dataRangeOp.value) count++
  if (startedOp.value) count++
  if (completedOp.value) count++
  if (numAdsFilter.value) count++
  if (selectedCountries.value.length) count++
  if (selectedSectors.value.length) count++
  if (selectedClients.value.length) count++
  return count
})

// Table sorting (server-side)
const { sorting, toggleSort } = useSortableTable({
  initialSort: { id: 'scheduled_at', desc: true },
  syncToUrl: true,
  onSortChange: () => {
    currentPage.value = 1
    void fetchData()
  },
})

// Num ads filter options
const numAdsOptions: { value: 'has_ads' | 'no_ads'; label: string }[] = [
  { value: 'has_ads', label: '✅ > 0 ads' },
  { value: 'no_ads', label: '❌ = 0 ads' },
]

// Custom sortable header for server-side sorting (must match TanStack signature)
function sortableHeader(label: string, columnId: string) {
  return ({ column: _column }: { column: unknown }) => {
    void _column // unused - we use our own sorting state for server-side
    const isActive = sorting.value?.id === columnId
    const isDesc = sorting.value?.desc
    const icon = isActive
      ? isDesc
        ? 'i-lucide-arrow-down-wide-narrow'
        : 'i-lucide-arrow-up-narrow-wide'
      : 'i-lucide-arrow-up-down'

    return h(UButton, {
      color: 'neutral',
      variant: 'ghost',
      label,
      icon,
      class: '-mx-2.5',
      onClick: () => toggleSort(columnId),
    })
  }
}

// URL filters
useUrlFilters({
  filters: {
    search: searchQuery,
    platforms: selectedPlatforms,
    methods: selectedMethods,
    statuses: selectedStatuses,
    countries: selectedCountries,
    sectors: selectedSectors,
    clients: selectedClients,
    completed_op: completedOp,
    completed_date: completedDate,
    completed_date_end: completedDateEnd,
    started_op: startedOp,
    started_date: startedDate,
    started_date_end: startedDateEnd,
    data_range_op: dataRangeOp,
    data_range_from: dataRangeFrom,
    data_range_to: dataRangeTo,
    num_ads: numAdsFilter,
  },
  debounce: 300,
  onFilterChange: () => {
    currentPage.value = 1
    void fetchData()
  },
})

// Status options (subset shown in filter UI)
const statusOptions: ImportJobStatus[] = [
  'SCHEDULED',
  'RUNNING',
  'STORING_RESULTS',
  'RESULTS_FETCHED',
  'COMPLETED',
  'FAILED',
  'TIMED_OUT',
  'CANCELLED',
  'SUPERSEDED',
]
const validStatuses = new Set<string>(statusOptions)

// Filter invalid statuses from URL (only valid ImportJobStatus values)
const validatedStatuses = computed(() =>
  selectedStatuses.value.filter((s): s is ImportJobStatus => validStatuses.has(s)),
)

// UUID validation for hash-derived job IDs
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Filter by brand name (when clicking on brand in table)
function filterByBrand(brandName: string) {
  searchQuery.value = brandName
}

// Pagination
const currentPage = ref(1)
const pageSize = 50

// Import slideover state
const showImportSlideover = ref(false)
const selectedImport = ref<DataImportJobDetail | null>(null)
const isLoadingDetail = ref(false)
let loadingRequestId = 0 // Incrementing counter to track the most recent detail request
let fetchRequestId = 0 // Incrementing counter to guard concurrent fetchData calls

// Brand slideover state
const showBrandSlideover = ref(false)
const selectedBrand = ref<Brand | null>(null)

// Platform options (derived from admin platforms, root platforms only).
// Keep the full object so the template can render `platform.name` via
// formatPlatformName — passing `.code` alone loses the DB display name.
const platformOptions = computed(() => admin.platforms.filter((p) => !p.parent_platform_id))

// Country options for multi-select dropdown (selected items first)
const countryOptions = computed(() =>
  sortSelectedFirst(
    [
      { label: '🌍 ALL', code: 'ALL', value: 'ALL' },
      ...admin.countries.map((c) => ({
        label: `${c.emoji} ${c.name_en}`,
        code: c.code,
        value: c.code,
      })),
    ],
    selectedCountries.value,
  ),
)

// Sector options for multi-select dropdown (selected items first)
const sectorOptions = computed(() =>
  sortSelectedFirst(
    admin.sectors.map((s) => ({
      label: s.emoji ? `${s.emoji} ${s.name}` : s.name,
      slug: s.slug,
      value: s.slug,
    })),
    selectedSectors.value,
  ),
)

// Client (organization) options for multi-select dropdown (selected items first)
const clientOptions = computed(() =>
  sortSelectedFirst(
    admin.organizations.map((o) => ({
      label: o.name,
      value: o.slug,
    })),
    selectedClients.value,
  ),
)

// Table columns
const columns = computed<TableColumn<DataImportJob>[]>(() => {
  // Force dependency on sorting so headers re-render with correct icons
  void sorting.value
  return [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 180,
      cell: ({ row }) => copyableId(row.original.id),
    },
    {
      accessorKey: 'platform',
      header: sortableHeader('Platform', 'platform'),
      size: 90,
      cell: ({ row }) => {
        const code = row.original.platform
        const p = admin.platforms.find((x) => x.code === code)
        return h(PlatformBadge, { code, name: p?.name })
      },
    },
    {
      accessorKey: 'brand',
      header: sortableHeader('Brand', 'brand'),
      size: 150,
      cell: ({ row }) => {
        const brand = row.original.brand
        return h('div', { class: 'flex items-center justify-between' }, [
          // Brand name - clickable to filter
          brand
            ? h(
                'span',
                {
                  class: 'cursor-pointer hover:underline truncate',
                  onClick: () => filterByBrand(brand),
                },
                brand,
              )
            : h('span', '-'),
          // View button only if brand_id and brand_slug exist
          (() => {
            const slug = row.original.brand_slug
            return slug
              ? h(UButton, {
                  icon: 'i-lucide-eye',
                  size: 'xs',
                  variant: 'ghost',
                  color: 'neutral',
                  class: 'shrink-0 ml-2',
                  onClick: () => openBrandSlideover(slug),
                })
              : h('div', { class: 'w-6' }) // Placeholder to maintain alignment
          })(),
        ])
      },
    },
    {
      accessorKey: 'platform_advertiser_identifier',
      header: sortableHeader('Advertiser', 'platform_advertiser_identifier'),
      size: 110,
      cell: ({ row }) =>
        h(
          'span',
          { class: 'font-mono text-xs text-muted' },
          row.original.platform_advertiser_identifier || '-',
        ),
    },
    {
      accessorKey: 'country_code',
      header: sortableHeader('Country', 'country_code'),
      size: 100,
      cell: ({ row }) => {
        const code = row.original.country_code
        if (!code) return h('span', { class: 'text-muted' }, '-')
        const country = admin.countries.find((c) => c.code === code)
        const emoji = country?.emoji || '🌍'
        const name = country?.name_en || code.toUpperCase()
        return h('span', `${emoji} ${name}`)
      },
    },
    {
      accessorKey: 'date_from',
      header: sortableHeader('From', 'date_from'),
      size: 85,
      cell: ({ row }) => h('span', { class: 'text-sm text-muted' }, row.original.date_from || '-'),
    },
    {
      accessorKey: 'date_to',
      header: sortableHeader('To', 'date_to'),
      size: 85,
      cell: ({ row }) => h('span', { class: 'text-sm text-muted' }, row.original.date_to || '-'),
    },
    {
      accessorKey: 'status',
      header: sortableHeader('Status', 'status'),
      size: 95,
      cell: ({ row }) =>
        h(
          UBadge,
          { color: getImportStatusColor(row.original.status), size: 'sm' },
          () => row.original.status,
        ),
    },
    {
      accessorKey: 'run_methods',
      header: 'Method',
      size: 90,
      cell: ({ row }) => {
        const methods = row.original.run_methods || []
        if (!methods.length) return h('span', { class: 'text-muted' }, '-')
        // ponytail: keep best status per method (COMPLETED wins over failures)
        const byMethod = new Map<string, (typeof methods)[0]>()
        for (const m of methods) {
          const existing = byMethod.get(m.method)
          if (!existing || m.status === 'COMPLETED') {
            byMethod.set(m.method, m)
          }
        }
        const unique = [...byMethod.values()]
        return h(
          'div',
          { class: 'flex gap-1 flex-wrap' },
          unique.map((m) =>
            h(
              UBadge,
              {
                color: m.status === 'COMPLETED' ? 'success' : 'error',
                size: 'xs',
                variant: 'subtle',
              },
              () => m.method,
            ),
          ),
        )
      },
    },
    {
      accessorKey: 'task_ix',
      header: sortableHeader('Task', 'task_ix'),
      size: 55,
      cell: ({ row }) =>
        h(
          'span',
          { class: 'text-muted font-mono text-xs' },
          row.original.task_ix != null ? `#${row.original.task_ix}` : '-',
        ),
    },
    {
      accessorKey: 'num_ads',
      header: sortableHeader('Ads', 'num_ads'),
      size: 60,
      cell: ({ row }) =>
        h('span', { class: 'text-muted' }, row.original.num_ads?.toLocaleString() ?? '-'),
    },
    {
      accessorKey: 'started_ts',
      header: sortableHeader('Started', 'started_ts'),
      size: 85,
      cell: ({ row }) =>
        h('span', { class: 'text-sm text-muted' }, formatRelativeTime(row.original.started_ts)),
    },
    {
      accessorKey: 'completed_ts',
      header: sortableHeader('Completed', 'completed_ts'),
      size: 85,
      cell: ({ row }) =>
        h('span', { class: 'text-sm text-muted' }, formatRelativeTime(row.original.completed_ts)),
    },
    {
      id: 'links',
      header: 'Links',
      size: 80,
      cell: ({ row }) => {
        const links = []

        // Cloud Run link (Google Cloud Console)
        const cloudRunUrl = buildCloudRunUrl(
          row.original.cloud_run_job_id,
          row.original.cloud_run_execution_id,
        )
        if (cloudRunUrl) {
          links.push(
            h(UButton, {
              icon: 'i-lucide-cloud',
              size: 'xs',
              variant: 'ghost',
              color: 'neutral',
              as: 'a',
              href: cloudRunUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
              title: 'Google Cloud Run',
            }),
          )
        }

        // Cloud Run Logs link
        const cloudRunLogsUrl = buildCloudRunLogsUrl(
          row.original.cloud_run_job_id,
          row.original.cloud_run_execution_id,
          row.original.task_ix,
          row.original.scheduled_ts,
        )
        if (cloudRunLogsUrl) {
          links.push(
            h(UButton, {
              icon: 'i-lucide-file-text',
              size: 'xs',
              variant: 'ghost',
              color: 'neutral',
              as: 'a',
              href: cloudRunLogsUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
              title: 'Cloud Run Logs',
            }),
          )
        }

        // Apify link
        const apifyUrl = buildApifyUrl(row.original.apify_platform_run_id)
        if (apifyUrl) {
          links.push(
            h(UButton, {
              icon: 'i-lucide-bot',
              size: 'xs',
              variant: 'ghost',
              color: 'neutral',
              as: 'a',
              href: apifyUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
              title: 'Apify',
            }),
          )
        }

        // Ad Library link (scraping URL)
        if (row.original.scraping_url) {
          links.push(
            h(UButton, {
              icon: 'i-lucide-book-open',
              size: 'xs',
              variant: 'ghost',
              color: 'neutral',
              as: 'a',
              href: row.original.scraping_url,
              target: '_blank',
              rel: 'noopener noreferrer',
              title: 'Ad Library',
            }),
          )
        }

        if (links.length === 0) {
          return h('span', { class: 'text-muted' }, '-')
        }

        return h('div', { class: 'flex gap-1' }, links)
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 65,
      cell: ({ row }) =>
        h(
          UButton,
          {
            icon: 'i-lucide-eye',
            color: 'neutral',
            variant: 'ghost',
            size: 'sm',
            onClick: () => openImportDetail(row.original),
          },
          () => 'View',
        ),
    },
  ]
})

// Fetch data with current filters and sorting
async function fetchData() {
  const thisRequestId = ++fetchRequestId
  isLoading.value = true
  try {
    await imports.fetchImportJobs({
      page: currentPage.value,
      page_size: pageSize,
      search: searchQuery.value || undefined,
      platforms: selectedPlatforms.value.length ? selectedPlatforms.value : undefined,
      methods: selectedMethods.value.length ? selectedMethods.value : undefined,
      job_statuses: validatedStatuses.value.length ? validatedStatuses.value : undefined,
      countries: selectedCountries.value.length ? selectedCountries.value : undefined,
      sectors: selectedSectors.value.length ? selectedSectors.value : undefined,
      clients: selectedClients.value.length ? selectedClients.value : undefined,
      completed_op: completedOp.value || undefined,
      completed_date: completedDate.value || undefined,
      completed_date_end: completedDateEnd.value || undefined,
      started_op: startedOp.value || undefined,
      started_date: startedDate.value || undefined,
      started_date_end: startedDateEnd.value || undefined,
      data_range_from: resolvedDataRange.value.from,
      data_range_to: resolvedDataRange.value.to,
      num_ads: numAdsFilter.value || undefined,
      sort_by: sorting.value?.id || undefined,
      sort_desc: sorting.value ? sorting.value.desc : undefined,
    })
    // Ignore stale responses from superseded requests
    if (thisRequestId !== fetchRequestId) return
  } catch (err) {
    if (thisRequestId !== fetchRequestId) return
    handleError(err, 'Failed to load import jobs')
  } finally {
    // Only clear loading state for the latest request
    if (thisRequestId === fetchRequestId) {
      isLoading.value = false
    }
  }
}

// Platform filter
function togglePlatformFilter(platform: string) {
  const idx = selectedPlatforms.value.indexOf(platform)
  if (idx !== -1) {
    selectedPlatforms.value.splice(idx, 1)
  } else {
    selectedPlatforms.value.push(platform)
  }
}

// Method filter
const methodOptions = ['SCRAPING', 'APIFY', 'API'] as const
function toggleMethodFilter(method: string) {
  const idx = selectedMethods.value.indexOf(method)
  if (idx !== -1) {
    selectedMethods.value.splice(idx, 1)
  } else {
    selectedMethods.value.push(method)
  }
}

// Status filter
function toggleStatusFilter(status: ImportJobStatus) {
  const idx = selectedStatuses.value.indexOf(status)
  if (idx !== -1) {
    selectedStatuses.value.splice(idx, 1)
  } else {
    selectedStatuses.value.push(status)
  }
}

// Pagination
function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  void fetchData()
}

// Clear filter helpers
function clearPlatformFilter() {
  selectedPlatforms.value = []
}

function clearMethodFilter() {
  selectedMethods.value = []
}

function clearStatusFilter() {
  selectedStatuses.value = []
}

function clearCountryFilter() {
  selectedCountries.value = []
}

function clearSectorFilter() {
  selectedSectors.value = []
}

function clearClientFilter() {
  selectedClients.value = []
}

function clearSearch() {
  searchQuery.value = ''
}

function clearDataRangeFilter() {
  dataRangeOp.value = ''
  dataRangeFrom.value = ''
  dataRangeTo.value = ''
}

function clearStartedFilter() {
  startedOp.value = ''
  startedDate.value = ''
  startedDateEnd.value = ''
}

function clearCompletedFilter() {
  completedOp.value = ''
  completedDate.value = ''
  completedDateEnd.value = ''
}

// Open import detail slideover with hash navigation
// fromHashChange: true when called from hashchange handler (use replaceState to avoid history loop)
async function openImportDetail(job: DataImportJob, fromHashChange = false) {
  const isReplacing = showImportSlideover.value || fromHashChange
  showImportSlideover.value = true
  isLoadingDetail.value = true
  selectedImport.value = null // Clear stale data while loading

  // Increment counter to track this request; capture for later comparison
  const thisRequestId = ++loadingRequestId

  // Update URL hash
  if (isReplacing) {
    history.replaceState(null, '', `#job-${job.id}`)
  } else {
    history.pushState(null, '', `#job-${job.id}`)
  }

  try {
    const detail = await imports.fetchImportJobDetail(job.id)
    // Only update if this is still the most recent request
    if (thisRequestId === loadingRequestId) {
      selectedImport.value = detail
    }
  } catch (err) {
    // Only handle error if this is still the most recent request
    if (thisRequestId === loadingRequestId) {
      handleError(err, 'Failed to load import details')
      showImportSlideover.value = false
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  } finally {
    if (thisRequestId === loadingRequestId) {
      isLoadingDetail.value = false
    }
  }
}

function closeImportSlideover() {
  showImportSlideover.value = false
  // State cleanup handled by watch on showImportSlideover
}

// Load import by ID when not in current page results
function loadImportById(jobId: string) {
  showImportSlideover.value = true
  isLoadingDetail.value = true
  const thisRequestId = ++loadingRequestId
  imports
    .fetchImportJobDetail(jobId)
    .then((detail) => {
      if (thisRequestId === loadingRequestId) {
        selectedImport.value = detail
      }
    })
    .catch((err) => {
      if (thisRequestId === loadingRequestId) {
        handleError(err, 'Failed to load import details')
        showImportSlideover.value = false
        history.replaceState(null, '', window.location.pathname + window.location.search)
      }
    })
    .finally(() => {
      if (thisRequestId === loadingRequestId) {
        isLoadingDetail.value = false
      }
    })
}

// Open brand slideover with hash navigation
function openBrandSlideover(brandSlug: string) {
  const brand = admin.brands.find((b) => b.slug === brandSlug)
  if (!brand) {
    handleError(new Error('Brand not found'), 'Could not find brand')
    history.replaceState(null, '', window.location.pathname + window.location.search)
    return
  }

  const isReplacing = showBrandSlideover.value || !!window.location.hash
  selectedBrand.value = brand
  showBrandSlideover.value = true

  if (isReplacing) {
    history.replaceState(null, '', `#brand-${brand.slug}`)
  } else {
    history.pushState(null, '', `#brand-${brand.slug}`)
  }
}

function closeBrandSlideover() {
  showBrandSlideover.value = false
  selectedBrand.value = null
}

function handleBrandUpdated(brand: Brand) {
  selectedBrand.value = brand
}

// Clear hash and state when slideover closes (handles backdrop dismiss)
watch(showImportSlideover, (isOpen) => {
  if (!isOpen) {
    selectedImport.value = null
    isLoadingDetail.value = false
    loadingRequestId++ // Invalidate any in-flight requests
    if (window.location.hash.startsWith('#job-')) {
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }
})

watch(showBrandSlideover, (isOpen) => {
  if (!isOpen) {
    selectedBrand.value = null
    if (window.location.hash.startsWith('#brand-')) {
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }
})

// Handle browser back/forward
function handleHashChange() {
  const hash = window.location.hash.slice(1)
  if (!hash) {
    if (showImportSlideover.value) {
      showImportSlideover.value = false
      selectedImport.value = null
    }
    if (showBrandSlideover.value) {
      showBrandSlideover.value = false
      selectedBrand.value = null
    }
  } else if (hash.startsWith('job-')) {
    const jobId = hash.slice(4)
    if (!UUID_RE.test(jobId)) {
      history.replaceState(null, '', window.location.pathname + window.location.search)
      return
    }
    if (selectedImport.value?.id !== jobId) {
      const job = imports.importJobs.find((j) => j.id === jobId)
      if (job) {
        void openImportDetail(job, true)
      } else {
        loadImportById(jobId)
      }
    }
  } else if (hash.startsWith('brand-')) {
    const slug = hash.slice(6)
    if (selectedBrand.value?.slug !== slug) {
      openBrandSlideover(slug)
    }
  }
}

// Total pages
const totalPages = computed(() => Math.max(1, Math.ceil(imports.importJobsTotal / pageSize)))

// Initialize
onMounted(async () => {
  // Listen for hash changes (back/forward navigation) - register early so it works even if fetch fails
  window.addEventListener('hashchange', handleHashChange)

  try {
    // Note: URL filters are initialized by useUrlFilters composable
    await Promise.all([
      fetchData(),
      admin.fetchPlatforms(),
      admin.fetchBrands(),
      admin.fetchCountries(),
      admin.fetchSectors(),
      admin.fetchOrganizations(),
    ])

    // Check for hash to open a specific import or brand
    const hash = window.location.hash.slice(1)
    if (hash.startsWith('job-')) {
      const jobId = hash.slice(4)
      if (!UUID_RE.test(jobId)) {
        history.replaceState(null, '', window.location.pathname + window.location.search)
        return
      }
      const job = imports.importJobs.find((j) => j.id === jobId)
      if (job) {
        void openImportDetail(job, true)
      } else {
        loadImportById(jobId)
      }
    } else if (hash.startsWith('brand-')) {
      const slug = hash.slice(6)
      openBrandSlideover(slug)
    }
  } catch (err) {
    handleError(err, 'Failed to load data')
    showImportSlideover.value = false
    isLoadingDetail.value = false
  }
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})
</script>

<template>
  <AppComponent title="Imports" id="admin-imports">
    <template #actions>
      <span class="text-sm text-muted">{{ imports.importJobsTotal.toLocaleString() }} jobs</span>
      <UButton
        icon="i-lucide-refresh-cw"
        color="neutral"
        variant="ghost"
        :loading="isLoading"
        @click="fetchData"
      />
    </template>

    <div class="h-full flex flex-col">
      <!-- Filters (sticky) -->
      <div class="p-4 space-y-2 shrink-0 bg-[var(--ui-bg)] border-b border-[var(--ui-border)]">
        <!-- Platform filter -->
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Platform</span>
          <div class="flex gap-1 flex-wrap">
            <UButton
              v-for="platform in platformOptions"
              :key="platform.code"
              :color="selectedPlatforms.includes(platform.code) ? 'primary' : 'neutral'"
              :variant="selectedPlatforms.includes(platform.code) ? 'soft' : 'outline'"
              size="sm"
              @click="togglePlatformFilter(platform.code)"
            >
              {{ formatPlatformName(platform.name) }}
            </UButton>
          </div>
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="xs"
            :class="selectedPlatforms.length ? '' : 'opacity-0 pointer-events-none'"
            aria-label="Clear platform filter"
            @click="clearPlatformFilter"
          />
          <!-- Method (inline on xl+) -->
          <div class="hidden xl:flex items-center gap-2 ml-4">
            <span class="text-sm text-muted">Method</span>
            <div class="flex gap-1">
              <UButton
                v-for="method in methodOptions"
                :key="method"
                :color="selectedMethods.includes(method) ? 'primary' : 'neutral'"
                :variant="selectedMethods.includes(method) ? 'soft' : 'outline'"
                size="sm"
                @click="toggleMethodFilter(method)"
              >
                {{ method }}
              </UButton>
            </div>
            <UButton
              icon="i-lucide-x"
              color="neutral"
              variant="ghost"
              size="xs"
              :class="selectedMethods.length ? '' : 'opacity-0 pointer-events-none'"
              aria-label="Clear method filter"
              @click="clearMethodFilter"
            />
          </div>
        </div>

        <!-- Method filter (own row on <xl) -->
        <div class="flex xl:hidden items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Method</span>
          <div class="flex gap-1 flex-wrap">
            <UButton
              v-for="method in methodOptions"
              :key="method"
              :color="selectedMethods.includes(method) ? 'primary' : 'neutral'"
              :variant="selectedMethods.includes(method) ? 'soft' : 'outline'"
              size="sm"
              @click="toggleMethodFilter(method)"
            >
              {{ method }}
            </UButton>
          </div>
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="xs"
            :class="selectedMethods.length ? '' : 'opacity-0 pointer-events-none'"
            aria-label="Clear method filter"
            @click="clearMethodFilter"
          />
        </div>

        <!-- Status filter -->
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Status</span>
          <div class="flex gap-1 flex-wrap">
            <UButton
              v-for="status in statusOptions"
              :key="status"
              :color="selectedStatuses.includes(status) ? getImportStatusColor(status) : 'neutral'"
              :variant="selectedStatuses.includes(status) ? 'soft' : 'outline'"
              size="sm"
              @click="toggleStatusFilter(status)"
            >
              {{ status }}
            </UButton>
          </div>
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="xs"
            :class="selectedStatuses.length ? '' : 'opacity-0 pointer-events-none'"
            aria-label="Clear status filter"
            @click="clearStatusFilter"
          />
        </div>

        <!-- Search + Toggle more filters -->
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Search</span>
          <UInput
            v-model="searchQuery"
            icon="i-lucide-search"
            placeholder="Brand or advertiser ID..."
            :ui="searchQuery ? activeFilterUi : undefined"
            class="w-64"
          >
            <template v-if="searchQuery" #trailing>
              <UButton
                icon="i-lucide-x"
                color="neutral"
                variant="ghost"
                size="xs"
                @click="clearSearch"
              />
            </template>
          </UInput>
          <div class="flex-1" />
          <UButton
            :icon="showMoreFilters ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            color="neutral"
            variant="ghost"
            size="sm"
            @click="showMoreFilters = !showMoreFilters"
          >
            <span class="hidden sm:inline">{{ showMoreFilters ? 'Less' : 'More' }}</span>
          </UButton>
          <UBadge v-if="!showMoreFilters && hiddenFiltersCount > 0" size="xs" color="primary">
            {{ hiddenFiltersCount }}
          </UBadge>
        </div>

        <!-- Hidden filters: grid layout -->
        <template v-if="showMoreFilters">
          <!-- Ad count filter -->
          <div class="flex items-center gap-2">
            <span class="text-sm text-muted w-36 shrink-0">Ad count</span>
            <div class="flex gap-1 flex-wrap">
              <UButton
                v-for="opt in numAdsOptions"
                :key="opt.value"
                :color="numAdsFilter === opt.value ? 'primary' : 'neutral'"
                :variant="numAdsFilter === opt.value ? 'soft' : 'outline'"
                size="sm"
                @click="numAdsFilter = numAdsFilter === opt.value ? '' : opt.value"
              >
                {{ opt.label }}
              </UButton>
            </div>
            <UButton
              icon="i-lucide-x"
              color="neutral"
              variant="ghost"
              size="xs"
              :class="numAdsFilter ? '' : 'opacity-0 pointer-events-none'"
              aria-label="Clear ad count filter"
              @click="numAdsFilter = ''"
            />
          </div>

          <!-- Dates row -->
          <div class="flex items-start gap-2">
            <span class="text-sm text-muted w-36 shrink-0 pt-1.5">Dates</span>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-10 gap-y-2">
              <div class="flex items-center gap-1">
                <span class="text-sm text-muted w-24 shrink-0">Data range</span>
                <SmartDateFilter
                  v-model:operator="dataRangeOp"
                  v-model:date="dataRangeFrom"
                  v-model:date-end="dataRangeTo"
                />
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :class="dataRangeOp ? '' : 'opacity-0 pointer-events-none'"
                  aria-label="Clear data range filter"
                  @click="clearDataRangeFilter"
                />
              </div>
              <div class="flex items-center gap-1">
                <span class="text-sm text-muted w-24 shrink-0">Started</span>
                <SmartDateFilter
                  v-model:operator="startedOp"
                  v-model:date="startedDate"
                  v-model:date-end="startedDateEnd"
                />
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :class="startedOp ? '' : 'opacity-0 pointer-events-none'"
                  aria-label="Clear started filter"
                  @click="clearStartedFilter"
                />
              </div>
              <div class="flex items-center gap-1">
                <span class="text-sm text-muted w-24 shrink-0">Completed</span>
                <SmartDateFilter
                  v-model:operator="completedOp"
                  v-model:date="completedDate"
                  v-model:date-end="completedDateEnd"
                />
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :class="completedOp ? '' : 'opacity-0 pointer-events-none'"
                  aria-label="Clear completed filter"
                  @click="clearCompletedFilter"
                />
              </div>
            </div>
          </div>

          <!-- Others row -->
          <div class="flex items-start gap-2">
            <span class="text-sm text-muted w-36 shrink-0 pt-1.5">Others</span>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-10 gap-y-2">
              <div class="flex items-center gap-1">
                <span class="text-sm text-muted w-24 shrink-0">Country</span>
                <USelectMenu
                  v-model="selectedCountries"
                  :items="countryOptions"
                  :ui="selectedCountries.length ? activeFilterUi : undefined"
                  size="sm"
                  multiple
                  placeholder="All"
                  value-key="value"
                  :filter-fields="['label', 'code']"
                  class="w-32"
                />
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :class="selectedCountries.length ? '' : 'opacity-0 pointer-events-none'"
                  aria-label="Clear country filter"
                  @click="clearCountryFilter"
                />
              </div>
              <div class="flex items-center gap-1">
                <span class="text-sm text-muted w-24 shrink-0">Sector</span>
                <USelectMenu
                  v-model="selectedSectors"
                  :items="sectorOptions"
                  :ui="selectedSectors.length ? activeFilterUi : undefined"
                  size="sm"
                  multiple
                  placeholder="All"
                  value-key="value"
                  :filter-fields="['label', 'slug']"
                  class="w-32"
                />
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :class="selectedSectors.length ? '' : 'opacity-0 pointer-events-none'"
                  aria-label="Clear sector filter"
                  @click="clearSectorFilter"
                />
              </div>
              <div class="flex items-center gap-1">
                <span class="text-sm text-muted w-24 shrink-0">Client</span>
                <USelectMenu
                  v-model="selectedClients"
                  :items="clientOptions"
                  :ui="selectedClients.length ? activeFilterUi : undefined"
                  size="sm"
                  multiple
                  placeholder="All"
                  value-key="value"
                  :filter-fields="['label']"
                  class="w-32"
                />
                <UButton
                  icon="i-lucide-x"
                  color="neutral"
                  variant="ghost"
                  size="xs"
                  :class="selectedClients.length ? '' : 'opacity-0 pointer-events-none'"
                  aria-label="Clear client filter"
                  @click="clearClientFilter"
                />
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Table area -->
      <div class="flex-1 min-h-0 overflow-auto sticky-table-header">
        <UTable :columns="columns" :data="imports.importJobs" :loading="isLoading">
          <template #empty>
            <div class="text-center py-6 text-muted">
              {{ isLoading ? 'Loading data...' : 'No data' }}
            </div>
          </template>
        </UTable>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex items-center justify-between p-4">
          <span class="text-sm text-muted">
            Page {{ currentPage }} of {{ totalPages }} ({{
              imports.importJobsTotal.toLocaleString()
            }}
            total)
          </span>
          <div class="flex gap-1">
            <UButton
              icon="i-lucide-chevron-left"
              color="neutral"
              variant="outline"
              size="sm"
              :disabled="currentPage <= 1"
              @click="goToPage(currentPage - 1)"
            />
            <UButton
              icon="i-lucide-chevron-right"
              color="neutral"
              variant="outline"
              size="sm"
              :disabled="currentPage >= totalPages"
              @click="goToPage(currentPage + 1)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Import Detail Slideover -->
    <USlideover v-model:open="showImportSlideover" side="right">
      <template #content>
        <div v-if="isLoadingDetail" class="p-6 flex items-center justify-center">
          <UIcon name="i-lucide-loader-2" class="animate-spin text-2xl" />
        </div>
        <AdminImportDetail
          v-else-if="selectedImport"
          :import-job="selectedImport"
          @close="closeImportSlideover"
          @view-brand="openBrandSlideover"
        />
      </template>
    </USlideover>

    <!-- Brand Detail Slideover -->
    <USlideover v-model:open="showBrandSlideover" side="right">
      <template #content>
        <AdminBrandDetail
          v-if="selectedBrand"
          :brand="selectedBrand"
          @close="closeBrandSlideover"
          @deleted="closeBrandSlideover"
          @updated="handleBrandUpdated"
        />
      </template>
    </USlideover>
  </AppComponent>
</template>
